/**
 * Wave 3 · B7 — TabsHost: renders the open tabs, only the active one visible.
 * See AppRoutes.tsx's docblock for why each tab is a `<Routes location=...>`
 * rather than a nested Router.
 *
 * UI overhaul PR-1a (audit APP-01/02/03/05) — three rules this file now keeps:
 *
 *   1. A pane's element tree never changes shape on activate/deactivate.
 *      react-router 7 wraps the matched routes in a `LocationContext.Provider`
 *      ONLY when `<Routes location>` is given an argument. The old code passed
 *      `undefined` to the active pane and a string to hidden ones, so every
 *      switch flipped the element type under `<Suspense>` and REMOUNTED the
 *      page — the "switching tabs reloads everything" the audit measured.
 *      Now every pane always receives a location OBJECT: the live one for the
 *      active pane, a memoized snapshot for hidden panes (`TabPane`).
 *   2. Only `LIVE_TABS` panes stay mounted: the active tab plus the most
 *      recently active ones (`mru.ts`). Older tabs are pills in the bar and
 *      re-render from scratch when clicked — the cost of a click, not a
 *      permanent 10k-node hidden Products page.
 *   3. Each pane is its own scroll container and remembers its scroll offset
 *      (`scrollMemory.ts`). Before, every pane shared `<main>`'s scrollbar, so a
 *      new page opened wherever the last one had been scrolled to.
 *   4. A hidden pane is INERT (PR-1b). Each pane sits in a React `Activity`
 *      boundary: while hidden, React unmounts every effect in the subtree —
 *      polls, listeners, resize observers, query subscriptions — and keeps
 *      the state and DOM. Before, "nothing runs in a hidden tab" was a
 *      per-call-site promise (`useTabVisible` gates), so any page that
 *      forgot it kept polling behind the active one. On return the effects
 *      re-run: TanStack re-subscribes each query and refetches only what
 *      went stale (`lib/query/queryClient.ts` staleTime), so a quick round
 *      trip costs zero requests and a long one refreshes the page once. A
 *      pane that mounts hidden (every live pane after an operator-scope
 *      change) is rendered at idle priority — its tree, DOM and lazy chunks
 *      are prepared — but runs no effects, so it fetches nothing until it is
 *      shown. One consequence, on purpose:
 *      a request still in flight when its pane hides is cancelled with it
 *      (TanStack cancels a query whose last observer leaves), so a page left
 *      mid-load starts that load again on return, and the active pane keeps
 *      the browser's six connections for itself meanwhile.
 *
 * Scope remount: the pre-tabs shell keyed `<main>` on the operator-scope
 * token so every page refetches under the new `X-Operator-Scope` header when
 * the operator switcher changes (AppShell's comment in App.tsx explains the
 * full rationale). That has to keep working per tab — switching operators
 * should invalidate ALL open tabs' data, not just the active one — so each
 * pane is keyed on `${tab.id}::${scope}`, forcing exactly that tab's subtree
 * to remount (and refetch) on a scope change while leaving the tab LIST itself
 * untouched.
 */
import {
  useLocation, UNSAFE_NavigationContext as NavigationContext, type Location,
} from 'react-router-dom'
import {
  Activity, memo, useContext, useLayoutEffect, useMemo, useRef, useState, type ContextType, type UIEvent,
} from 'react'
import AppRoutes, { RouteErrorBoundary } from '../AppRoutes'
import { useTabs } from './TabsContext'
import { TabActiveContext } from './useTabVisible'
import { usePortfolioScope } from '../lib/usePortfolioScope'
import { LIVE_TABS, type TabRecord } from './types'
import { pruneMru, touchMru } from './mru'
import { recallScroll, rememberScroll } from './scrollMemory'

type Navigation = ContextType<typeof NavigationContext>

export default function TabsHost() {
  const location = useLocation()
  const { tabs, activeKey } = useTabs()
  const { scope } = usePortfolioScope()

  // ── Hidden panes must not be able to drive the real address bar ──────────
  // `<Routes location=...>` overrides only the READ side (LocationContext) for
  // an inactive pane — react-router's useRoutesImpl installs a
  // LocationContext.Provider and nothing else. NavigationContext (the WRITE
  // side) still points at the single top-level BrowserRouter, so anything a
  // hidden, keep-alive pane does with useNavigate / setSearchParams /
  // <Navigate> hijacks the whole app's URL. That is not hypothetical: a
  // backgrounded tab parked on a redirect route (/runs, /settings,
  // /inventory/shipments, … — ~30 of them in AppRoutes.tsx) re-fires its
  // <Navigate replace> on every mount, and a page whose async "am I still
  // mounted?" guard used to be satisfied by UNMOUNTING (keep-alive means it
  // never unmounts) writes the URL long after the operator has clicked away.
  // Handing inactive panes an inert navigator restores the pre-tabs contract
  // — a page you have navigated away from cannot move you — in ONE place,
  // instead of one hand-written `isActivePill` guard per call site.
  const nav = useContext(NavigationContext)
  const inertNav = useMemo<Navigation>(() => ({
    ...nav,
    navigator: { ...nav.navigator, push: () => {}, replace: () => {}, go: () => {} },
  }), [nav])

  // ── Which tabs stay mounted: the active one + the most recent (LIVE_TABS) ─
  // Adjusted during render (React's documented "derive state from a prop
  // change" pattern) rather than in an effect, so the first render after a
  // switch already mounts the right set and nothing sets state in an effect.
  const alive = useMemo(() => new Set(tabs.map((t) => t.id)), [tabs])
  const [live, setLive] = useState<string[]>(() => [activeKey])
  const [liveFor, setLiveFor] = useState(activeKey)
  if (liveFor !== activeKey) {
    setLiveFor(activeKey)
    // Closed tabs leave the list before the new one is fronted, so a live
    // slot is never held by a tab that no longer exists.
    setLive(touchMru(pruneMru(live, alive, activeKey), activeKey, LIVE_TABS))
  }
  const mounted = pruneMru(live, alive, activeKey)

  // The live location always gets a pane, even in the one render before
  // TabsProvider's reconciliation effect commits it into the persisted
  // `tabs` array (new record navigations) — otherwise there'd be a one-frame
  // blank flash. Once the effect runs, this synthetic entry is replaced by
  // the real (same-id) tab and React reconciles them as the same element.
  const renderTabs = useMemo<TabRecord[]>(() => {
    if (tabs.some((t) => t.id === activeKey)) return tabs
    return [...tabs, {
      id: activeKey,
      title: '',
      location: { pathname: location.pathname, search: location.search, hash: location.hash },
      pinned: false,
      // This entry is a pure rendering fallback — it's never read from
      // context (TabBar sources its list from useTabs().tabs, not this
      // array), so lastActiveAt carries no real meaning here; a fixed
      // sentinel keeps the memo body a pure function of its deps.
      lastActiveAt: 0,
    }]
  }, [tabs, activeKey, location.pathname, location.search, location.hash])

  return (
    <>
      {renderTabs.map((tab) => {
        const isActive = tab.id === activeKey
        // Not among the live set -> no pane, no mount, no fetch.
        if (!isActive && !mounted.includes(tab.id)) return null
        return (
          <TabPane
            key={`${tab.id}::${scope}`}
            tab={tab}
            isActive={isActive}
            liveLocation={isActive ? location : null}
            nav={isActive ? nav : inertNav}
          />
        )
      })}
    </>
  )
}

/** Memoized so a navigation in the ACTIVE pane does not re-render the hidden
 *  ones: a hidden pane receives no live location (`null`), an inert navigator
 *  whose identity only changes with the router's, and its own tab record
 *  (`reconcileTabs` keeps untouched records by reference) — none of which
 *  move on an ordinary route change. react-router rebuilds its location
 *  context value on every render of `<Routes location>`, so memoizing the
 *  location object alone would not keep a hidden pane's `useLocation()`
 *  consumers quiet; not rendering the pane does. */
const TabPane = memo(function TabPane({
  tab, isActive, liveLocation, nav,
}: {
  tab: TabRecord
  isActive: boolean
  /** The router's live location for the active pane; null when hidden. */
  liveLocation: Location | null
  nav: Navigation
}) {
  const { pathname, search, hash } = tab.location
  // A hidden pane keeps rendering the location it was last on — as a
  // location OBJECT, so the element tree keeps its shape (rule 1 above).
  const frozen = useMemo<Partial<Location>>(
    () => ({ pathname, search, hash, state: null, key: tab.id }),
    [pathname, search, hash, tab.id],
  )

  const paneRef = useRef<HTMLDivElement>(null)
  // Restore this tab's scroll offset the moment it becomes the visible pane.
  // `Activity` hides a pane with `display:none`, and a `display:none` box
  // forgets its scroll position, which is why the offset is kept here rather
  // than trusted to the DOM. Effects inside a hidden Activity are unmounted
  // and re-run on show, so this runs on every return — as a layout effect:
  // after React has unhidden the box, before paint, so nothing jumps. New
  // tab -> 0.
  useLayoutEffect(() => {
    if (!isActive) return
    const el = paneRef.current
    if (el) el.scrollTop = recallScroll(tab.id)
  }, [isActive, tab.id])
  const onScroll = (e: UIEvent<HTMLDivElement>) => {
    rememberScroll(tab.id, e.currentTarget.scrollTop)
  }

  return (
    // Visibility belongs to Activity alone (rule 4): it hides the pane's box
    // and freezes everything inside it in one move. A hand-written
    // `display:none` next to it would hide the box without stopping the
    // page, which is exactly the half-measure this replaces.
    <Activity mode={isActive ? 'visible' : 'hidden'}>
      <div
        ref={paneRef}
        data-tab-pane={tab.id}
        onScroll={onScroll}
        className="relative h-full overflow-auto"
      >
        {/* The providers are ALWAYS present and only their values change.
            Rendering one conditionally would swap the child's element type on
            every activate/deactivate — React reconciles by type at a position,
            and that unmounts and remounts the whole pane. */}
        <NavigationContext.Provider value={nav}>
          <TabActiveContext.Provider value={isActive}>
            <RouteErrorBoundary resetKey={pathname}>
              <AppRoutes location={liveLocation ?? frozen} />
            </RouteErrorBoundary>
          </TabActiveContext.Provider>
        </NavigationContext.Provider>
      </div>
    </Activity>
  )
})
